export const renamePayeePrompt = {
  contextPrefix: `
The following is a list of transactions the user explicitly selected for payee cleanup.

Each transaction includes its current payee, date, amount, and current category. Use ONLY this data - do not look up or invent information about the merchant.
`,
  prompt: `
You are proposing replacement payee names for a list of transactions the user selected for a "rename payee" action.

Rules:
- Return exactly one proposal for every input transaction.
- The proposed payee must always be a slight variant of the current payee, not an exact copy.
- Keep the proposal clearly tied to the original payee. Clean it up, normalize it, or lightly expand it, but do not replace it with a different merchant.
- Prefer a polished display-name version of the same payee. When the current payee is noisy, remove raw bank-descriptor clutter, abbreviations, store numbers, trailing IDs, or payment-network prefixes.
- When the current payee already looks fairly clean, still return a lightly modified variant. Prefer a small display-oriented change such as better capitalization, punctuation, spacing, or a short clarifying suffix grounded in the provided category.
- If you add a clarifying suffix, keep it generic and category-aligned, such as "Groceries", "Dining", "Fuel", or "Shopping". Do not invent specific facts about the merchant.
- Never guess a different merchant, location, brand family, or business type unless it is already strongly implied by the current payee text itself.
- Do not change category, amount, or date. This action only proposes payee renames.
- Do not return an empty proposals array. The user asked to rename the selected payees, so every selected transaction needs a best-effort same-merchant variant.

Return a JSON object with a single "proposals" key: an array of objects, each with:
- "transactionId": the exact transaction ID from the input, unchanged
- "proposedPayee": the renamed payee, which must differ slightly from the current payee text
- "confidence": a number from 0 to 1 indicating how confident you are the proposed name is correct
- "rationale": one short sentence explaining the change

Examples:
- "SQ *COFFEE SHOP 4421" -> "Coffee Shop"
- "TRADER JOES #118" -> "Trader Joe's Groceries"
- "Trader Joe's" -> "Trader Joe's Groceries"
- "Shell" -> "Shell Fuel"
`,
  schema: {
    name: 'RenamePayeeProposalResponse',
    schema: {
      type: 'object',
      additionalProperties: false,
      strict: true,
      properties: {
        proposals: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              transactionId: { type: 'string' },
              proposedPayee: { type: 'string' },
              confidence: { type: 'number' },
              rationale: { type: 'string' },
            },
            required: ['transactionId', 'proposedPayee', 'confidence', 'rationale'],
          },
        },
      },
      required: ['proposals'],
    },
  },
};
